import React, { useState } from 'react'
import { Feature, Interval, planMoney, PriceText } from '../billing_sales/Plan'
import { Button } from '../ui/button'
import { SalesBasedPlan, PremiumPlanV2, StandardPlanV2 } from '../billing_sales/plans'
import { useUser } from '../../hooks/useUser'
import { ConfirmationDrawer } from './ConfirmationDrawer'
import { IntervalToggle } from '../billing_sales/SalesBasedBilling'
import { Popover } from '../ui/popover'
import { money, number } from '../../util/formatters'
import { Marketplace, Marketplaces } from '../../util/marketplaces'

export const SpendBasedBilling = () => {
    const [interval, setInterval] = useState(Interval.Yearly)
    const [selectedPlan, setSelectedPlan] = useState<null | SalesBasedPlan>(null)
    const { activeSubscription } = useUser()
    const nowUnix = Math.round(new Date().getTime() / 1000)
    const trialEnd = new Date((activeSubscription?.trial_end ?? nowUnix) * 1000)

    const handleSubscribe = (plan: SalesBasedPlan) => {
        setSelectedPlan(plan)
    }

    return (
        <div className="mb-40">
            <IntervalToggle interval={interval} setInterval={setInterval} />

            <div className="flex justify-center flex-fill gap-x-12">
                <PlanCard2
                    plan={StandardPlanV2}
                    interval={interval}
                    onSubscribe={handleSubscribe}
                />
                <PlanCard2 plan={PremiumPlanV2} interval={interval} onSubscribe={handleSubscribe} />
            </div>

            <ConfirmationDrawer
                trial_end_date={trialEnd}
                plan={selectedPlan}
                setPlan={setSelectedPlan}
                trial_days={14}
                interval={interval}
            />
        </div>
    )
}

interface PlanCard2Props {
    interval: Interval
    plan: SalesBasedPlan
    onSubscribe: (plan: SalesBasedPlan) => void
}

export const PlanCard2 = ({ interval, plan, onSubscribe }: PlanCard2Props) => {
    return (
        <div
            style={{ maxWidth: '400px' }}
            className="p-4 rounded-md border flex flex-col flex-grow-1 bg-white shadow-md"
        >
            <div className="text-xl font-medium text-center">{plan.name}</div>
            <div className="text-center mt-4 mb-8">{plan.description}</div>
            <Price plan={plan} interval={interval} />

            <div className="px-4">
                <Button
                    fill
                    large
                    text={'Subscribe to ' + plan.name}
                    className="my-6"
                    intent="success"
                    onClick={() => onSubscribe(plan)}
                />
            </div>

            <Feature>
                <div className="flex flex-col">
                    <span className="flex">
                        Up to {planMoney(plan.included_spend)} per mo in included ad spend
                    </span>
                    <span className="text-xs mt-1">
                        Overages billed at {plan.ad_spend_rates[interval] * 100}%
                        {interval == Interval.Yearly ? ' per month' : ''}
                    </span>
                </div>
            </Feature>
            <Feature>
                <div className="flex flex-col">{plan.historical_data}</div>
            </Feature>
            <Feature>
                <div className="flex flex-col">{plan.syncing}</div>
            </Feature>
            <Feature>
                <div className="flex flex-col">{plan.automations}</div>
            </Feature>
            <Feature>
                <div className="flex flex-col">
                    <div className="flex">
                        {number(plan.included_ad_accounts)} included&nbsp;
                        <Popover
                            interactionKind="hover"
                            content={<AccountToolip plan={plan} />}
                            className="border-b border-gray-700 cursor-pointer border-dashed"
                        >
                            ad accounts
                        </Popover>
                    </div>
                    {plan.ad_account_overage && (
                        <span className="text-xs mt-1">
                            Overages billed at {planMoney(plan.ad_account_overage?.price)}{' '}
                            {interval == Interval.Yearly ? 'per month ' : ''}per extra account
                        </span>
                    )}
                </div>
            </Feature>
            <Feature>
                <div className="flex flex-col">
                    <div className="flex">
                        {number(plan.included_ads)} included&nbsp;
                        <Popover
                            interactionKind="hover"
                            content={<AdsTooltip plan={plan} />}
                            className="border-b border-gray-700 cursor-pointer border-dashed"
                        >
                            ads
                        </Popover>
                    </div>
                    {plan.ads_overage && (
                        <span className="text-xs mt-1">
                            Overages billed at {planMoney(plan.ads_overage?.price)}{' '}
                            {interval == Interval.Yearly ? 'per month ' : ''}per{' '}
                            {plan.ads_overage?.amount / 1000}k ads
                        </span>
                    )}
                </div>
            </Feature>
        </div>
    )
}

const AccountToolip = ({ plan }: { plan: SalesBasedPlan }) => {
    return (
        <div className="p-3 max-w-md text-sm">
            An account is a single advertising account. Most sellers will have one advertising
            account for each marketplace that they sell in.
            <br />
            {!plan.ad_account_overage && (
                <>
                    <br />
                    This plan has a{' '}
                    <b>hard cap of {number(plan.included_ad_accounts)} ad accounts</b>. If you need
                    more ad accounts, you will need to upgrade to either the Standard or Premium
                    plan.
                </>
            )}
        </div>
    )
}

const AdsTooltip = ({ plan }: { plan: SalesBasedPlan }) => {
    const v = plan.included_ads + 0.9 * (plan.ads_overage?.amount ?? 0)
    const diff = v - plan.included_ads
    const count = Math.ceil(diff / (plan.ads_overage?.amount ?? 0))

    return (
        <div className="p-3 max-w-md text-sm">
            Merch Jar has extra charges for advertising accounts with a large number of ads. Ads are
            defined as the number of effectively enabled ads in your advertising account.
            <br />
            <br />
            Effectively enabled means that the ad itself, it's parent ad group, and it's parent
            campaign are all enabled.
            <br />
            <br />
            {plan.ads_overage && (
                <>
                    Example A:
                    <Table
                        v={[
                            ['Total Ads', number(plan.included_ads * 0.9) ?? ''],
                            ['Included Ads', number(-plan.included_ads) ?? ''],
                            ['Chargeable Ads', number(0)],
                            ['Overage Charge', m(0)],
                        ]}
                    />
                    <br />
                    Example B:
                    <Table
                        v={[
                            ['Total Ads', number(v)],
                            ['Included Ads', number(-plan.included_ads) ?? ''],
                            ['Chargeable Ads', number(diff)],
                            [
                                'Rate',
                                m(plan.ads_overage.price) +
                                    ' / ' +
                                    plan.ads_overage.amount / 1000 +
                                    'k ads',
                            ],
                            [
                                'Overage Charge',
                                number(count) +
                                    ' * ' +
                                    m(plan.ads_overage.price) +
                                    ' = ' +
                                    m(count * plan.ads_overage.price),
                            ],
                        ]}
                    />
                </>
            )}
            {!plan.ads_overage && (
                <div>
                    This plan has a <b>hard cap of {number(plan.included_ads)} ads</b>. If you need
                    more ads, you will need to upgrade to either the Standard or Premium plan.
                </div>
            )}
        </div>
    )
}

// A little helper to format the money as USD and ensure it is a string
const m = (v: number): string => {
    return money(v, Marketplaces[Marketplace.UnitedStates]) ?? ''
}

// Makes a properly right aligned table
const Table = ({ v }: { v: string[][] }) => {
    const maximum_row_length = v
        .map((row) => row.reduce((p, x) => p + x.length, 0) + 1)
        .reduce((a, b) => Math.max(a, b))

    const r = v.map((row) => {
        const row_length = row[0].length + row[1].length + 1
        if (row_length < maximum_row_length) {
            // Add padding
            row[0] += ' '.repeat(maximum_row_length - row_length + 1) + row[1]
        } else {
            row[0] += ' ' + row[1]
        }

        return row[0] + '\n'
    })

    return <pre className="text-xs bg-gray-100 p-1">{r.map((row) => row)}</pre>
}

const Price = ({ plan, interval }: { plan: SalesBasedPlan; interval: Interval }) => {
    if (interval == 'monthly') {
        return <PriceText amount={plan.monthly} />
    } else {
        return (
            <div className="flex flex-col items-center">
                <PriceText amount={plan.yearly / 12} />
                <div className="text-gray-400 text-xs">
                    Billed yearly at {planMoney(plan.yearly)} / year
                </div>
            </div>
        )
    }
}
